import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';

export interface RowAction {
  key: string;
  label: string;
  onSelect: () => void;
  /** Styled as the cautious action (Archive). */
  danger?: boolean;
}

/**
 * A "more actions" menu button (`aria-haspopup="menu"`): Down or click opens it, Up/Down/Home/End move, Enter or Space chooses,
 * Escape and Tab close it and Escape returns focus to the button. The accessible name says which role it belongs to.
 */
export function RowActionsMenu({ label, actions }: { label: string; actions: RowAction[] }) {
  const uid = useId();
  const [open, setOpen] = useState(false);
  // The list is positioned from the button (fixed), so the scrolling table around it never clips it.
  const [place, setPlace] = useState<{ right: number; top?: number; bottom?: number }>({ right: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const wrapRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!open) return;
    itemRefs.current[0]?.focus();
    function onPointerDown(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    // The list is fixed, so when the page or the table scrolls (or the window resizes) it follows its button.
    const follow = () => placeList();
    document.addEventListener('mousedown', onPointerDown);
    window.addEventListener('scroll', follow, true);
    window.addEventListener('resize', follow);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      window.removeEventListener('scroll', follow, true);
      window.removeEventListener('resize', follow);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function placeList() {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) return;
    const estimated = actions.length * 34 + 12;
    const flipUp = rect.bottom + estimated > window.innerHeight && rect.top > estimated;
    setPlace(flipUp
      ? { right: window.innerWidth - rect.right, bottom: window.innerHeight - rect.top + 4 }
      : { right: window.innerWidth - rect.right, top: rect.bottom + 4 });
  }

  function openList() {
    placeList();
    setOpen(true);
  }

  function close(returnFocus = true) {
    setOpen(false);
    if (returnFocus) buttonRef.current?.focus();
  }

  function onButtonKeyDown(e: KeyboardEvent<HTMLButtonElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      openList();
    }
  }

  function onMenuKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    const items = itemRefs.current.filter((x): x is HTMLButtonElement => x !== null);
    const index = items.indexOf(document.activeElement as HTMLButtonElement);
    if (e.key === 'ArrowDown') { e.preventDefault(); items[(index + 1) % items.length]?.focus(); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); items[(index - 1 + items.length) % items.length]?.focus(); }
    else if (e.key === 'Home') { e.preventDefault(); items[0]?.focus(); }
    else if (e.key === 'End') { e.preventDefault(); items[items.length - 1]?.focus(); }
    else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); close(); }
    else if (e.key === 'Tab') close(false);
  }

  return (
    <span className="jr-menu" ref={wrapRef}>
      <button
        ref={buttonRef}
        type="button"
        className="jr-btn jr-btn--secondary jr-btn--small"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? `${uid}-menu` : undefined}
        aria-label={label}
        onClick={() => (open ? setOpen(false) : openList())}
        onKeyDown={onButtonKeyDown}
      >
        Actions <span aria-hidden="true">{'▾'}</span>
      </button>
      {open && (
        <div id={`${uid}-menu`} className="jr-menu__list" role="menu" aria-label={label} onKeyDown={onMenuKeyDown} style={place}>
          {actions.map((a, i) => (
            <button
              key={a.key}
              ref={(el) => { itemRefs.current[i] = el; }}
              type="button"
              role="menuitem"
              className={a.danger ? 'jr-menu__item jr-menu__item--danger' : 'jr-menu__item'}
              // Focus goes back to the Actions button first, so a dialog this opens returns focus there when it closes.
              onClick={() => { close(true); a.onSelect(); }}
            >
              {a.label}
            </button>
          ))}
        </div>
      )}
    </span>
  );
}
